// NEC 2023 Table 310.16
// Allowable Ampacities of Insulated Conductors
// All temperature columns (60°C, 75°C, 90°C)
// Based on ambient temperature of 30°C
//
// NOTE: These are the printed Table 310.16 values. The NEC 240.4(D)
// small-conductor overcurrent limits (#14 Cu 15 A, #12 Cu 20 A, #10 Cu 30 A,
// #12 Al 15 A, #10 Al 25 A) are NOT ampacities and are NOT applied here; use
// getSmallConductorOcpdLimit() when selecting an overcurrent device. Motor
// circuits and other 240.4(G) applications are exempt from that limit.
// Per NEC 110.14(C) the usable ampacity is also limited by the termination
// temperature rating (usually 75°C); callers apply that cap.

import { ConductorMaterial, WireSize, WIRE_SIZES_ORDERED, TemperatureRating } from '../types';

interface AmpacityEntry {
  copper60C: number;
  copper75C: number;
  copper90C: number;
  aluminum60C: number;
  aluminum75C: number;
  aluminum90C: number;
}

// Wire size as internal representation -> ampacity values
// NEC 2023 Table 310.16
const TABLE_310_16: Record<number, AmpacityEntry> = {
  // AWG sizes (negative numbers)
  [-14]: { copper60C: 15, copper75C: 20, copper90C: 25, aluminum60C: 0, aluminum75C: 0, aluminum90C: 0 },
  [-12]: { copper60C: 20, copper75C: 25, copper90C: 30, aluminum60C: 15, aluminum75C: 20, aluminum90C: 25 },
  [-10]: { copper60C: 30, copper75C: 35, copper90C: 40, aluminum60C: 25, aluminum75C: 30, aluminum90C: 35 },
  [-8]: { copper60C: 40, copper75C: 50, copper90C: 55, aluminum60C: 35, aluminum75C: 40, aluminum90C: 45 },
  [-6]: { copper60C: 55, copper75C: 65, copper90C: 75, aluminum60C: 40, aluminum75C: 50, aluminum90C: 55 },
  [-4]: { copper60C: 70, copper75C: 85, copper90C: 95, aluminum60C: 55, aluminum75C: 65, aluminum90C: 75 },
  [-3]: { copper60C: 85, copper75C: 100, copper90C: 115, aluminum60C: 65, aluminum75C: 75, aluminum90C: 85 },
  [-2]: { copper60C: 95, copper75C: 115, copper90C: 130, aluminum60C: 75, aluminum75C: 90, aluminum90C: 100 },
  [-1]: { copper60C: 110, copper75C: 130, copper90C: 145, aluminum60C: 85, aluminum75C: 100, aluminum90C: 115 },
  // 0 and larger (1/0, 2/0, 3/0, 4/0)
  [0]: { copper60C: 125, copper75C: 150, copper90C: 170, aluminum60C: 100, aluminum75C: 120, aluminum90C: 135 },
  [0.5]: { copper60C: 145, copper75C: 175, copper90C: 195, aluminum60C: 115, aluminum75C: 135, aluminum90C: 150 },
  [0.75]: { copper60C: 165, copper75C: 200, copper90C: 225, aluminum60C: 130, aluminum75C: 155, aluminum90C: 175 },
  [0.9]: { copper60C: 195, copper75C: 230, copper90C: 260, aluminum60C: 150, aluminum75C: 180, aluminum90C: 205 },
  // kcmil sizes (positive numbers)
  [250]: { copper60C: 215, copper75C: 255, copper90C: 290, aluminum60C: 170, aluminum75C: 205, aluminum90C: 230 },
  [300]: { copper60C: 240, copper75C: 285, copper90C: 320, aluminum60C: 195, aluminum75C: 230, aluminum90C: 260 },
  [350]: { copper60C: 260, copper75C: 310, copper90C: 350, aluminum60C: 210, aluminum75C: 250, aluminum90C: 280 },
  [400]: { copper60C: 280, copper75C: 335, copper90C: 380, aluminum60C: 225, aluminum75C: 270, aluminum90C: 305 },
  [500]: { copper60C: 320, copper75C: 380, copper90C: 430, aluminum60C: 260, aluminum75C: 310, aluminum90C: 350 },
  [600]: { copper60C: 350, copper75C: 420, copper90C: 475, aluminum60C: 285, aluminum75C: 340, aluminum90C: 385 },
  [700]: { copper60C: 385, copper75C: 460, copper90C: 520, aluminum60C: 315, aluminum75C: 375, aluminum90C: 425 },
  [750]: { copper60C: 400, copper75C: 475, copper90C: 535, aluminum60C: 320, aluminum75C: 385, aluminum90C: 435 },
  [800]: { copper60C: 410, copper75C: 490, copper90C: 555, aluminum60C: 330, aluminum75C: 395, aluminum90C: 445 },
  [900]: { copper60C: 435, copper75C: 520, copper90C: 585, aluminum60C: 355, aluminum75C: 425, aluminum90C: 480 },
  [1000]: { copper60C: 455, copper75C: 545, copper90C: 615, aluminum60C: 375, aluminum75C: 445, aluminum90C: 500 },
  [1250]: { copper60C: 495, copper75C: 590, copper90C: 665, aluminum60C: 405, aluminum75C: 480, aluminum90C: 545 },
};

/**
 * Get base ampacity for a conductor
 * @param wireSize - Internal wire size representation
 * @param material - Conductor material (copper or aluminum)
 * @param tempRating - Temperature rating (60, 75, or 90°C). Defaults to 75°C per NEC 110.14(C)
 * @returns Base ampacity in amperes
 */
export function getBaseAmpacity(
  wireSize: number,
  material: ConductorMaterial,
  tempRating: TemperatureRating = 75
): number {
  const entry = TABLE_310_16[wireSize];
  if (!entry) {
    throw new Error(`Wire size ${wireSize} not found in Table 310.16`);
  }

  const key = `${material}${tempRating}C` as keyof AmpacityEntry;
  return entry[key];
}

/**
 * Find the minimum wire size that meets required ampacity
 * @param requiredAmpacity - Required ampacity in amperes
 * @param material - Conductor material
 * @param maxSize - Maximum conductor size to consider
 * @param tempRating - Temperature rating (60, 75, or 90°C). Defaults to 75°C
 * @returns Wire size that meets requirement, or null if none found
 */
export function findWireSizeForAmpacity(
  requiredAmpacity: number,
  material: ConductorMaterial,
  maxSize: WireSize = 1000,
  tempRating: TemperatureRating = 75
): WireSize | null {
  for (const size of WIRE_SIZES_ORDERED) {
    // Check if size exceeds max (for kcmil comparison)
    if (size > maxSize) continue;

    const ampacity = getBaseAmpacity(size, material, tempRating);
    if (ampacity >= requiredAmpacity) {
      return size;
    }
  }
  return null;
}

/**
 * NEC 240.4(D) small-conductor overcurrent protection limits.
 * Returns the maximum overcurrent device rating for #14, #12 and #10
 * conductors, or null when 240.4(D) does not apply to the size.
 * Not applicable to motor circuits and the other 240.4(G) applications.
 */
export function getSmallConductorOcpdLimit(
  wireSize: number,
  material: ConductorMaterial
): number | null {
  if (material === 'copper') {
    if (wireSize === -14) return 15;
    if (wireSize === -12) return 20;
    if (wireSize === -10) return 30;
    return null;
  }
  if (wireSize === -12) return 15;
  if (wireSize === -10) return 25;
  return null;
}

/**
 * Usable ampacity for sizing at a given conductor temperature rating with
 * the NEC 110.14(C) termination limit applied: the smaller of the conductor's
 * column value and the termination-rated column value.
 */
export function getTerminationLimitedAmpacity(
  wireSize: number,
  material: ConductorMaterial,
  tempRating: TemperatureRating = 75,
  terminationRating: TemperatureRating = 75
): number {
  const conductor = getBaseAmpacity(wireSize, material, tempRating);
  const termination = getBaseAmpacity(wireSize, material, terminationRating);
  return Math.min(conductor, termination);
}

export const NEC_240_4_D_REF = {
  table: '240.4(D)',
  article: '240.4(D)',
  description: 'Small Conductors - maximum overcurrent protection for 14, 12 and 10 AWG (not applicable to motor circuits per 240.4(G))',
};

export const NEC_TABLE_310_16_REF = {
  table: 'Table 310.16',
  article: '310.16',
  description: 'Allowable Ampacities of Insulated Conductors Rated Up to and Including 2000 Volts, Based on Ambient Temperature of 30°C (86°F)',
};

export { TABLE_310_16 };
